import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { PageHeader, Card, Modal, Field, Btn, Pill, Empty, Notice, inputCls } from "@/dashboard/ui";
import { PhoneOutgoing, Trash2, Plus, ArrowRight, Pencil, PhoneCall, Globe, Settings2, CheckCircle2, XCircle, BarChart3 } from "lucide-react";

const EMPTY_FORM = {
  name: "", agent_id: "", lead_ids: [], schedule: "",
  max_attempts: 2, hours_start: "09:00", hours_end: "17:00", follow_up_rules: "",
};

const TASK_TONE = { pending_approval: "amber", queued: "black", calling: "amber", completed: "green", failed: "red", cancelled: "outline" };

function SmartSelect({ onApply }) {
  const [filters, setFilters] = useState({ source: "", status: "", batch_id: "" });
  const [options, setOptions] = useState({ sources: [] });
  const [batches, setBatches] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/calling-campaigns/eligible-leads").then((r) => setOptions({ sources: r.data.sources || [] })).catch(() => {});
    api.get("/leads/import/batches").then((r) => setBatches(r.data.batches || [])).catch(() => {});
  }, []);

  const run = async () => {
    setBusy(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/calling-campaigns/eligible-leads", { params });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sel = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50/60 p-3.5" data-testid="smart-select-panel">
      <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Smart selection</p>
      <p className="mt-1 text-[11px] text-neutral-500">Filter your CRM to pick leads. Selecting never starts calls — every campaign stays a draft until you approve it.</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <select className={inputCls} value={filters.source} onChange={sel("source")} data-testid="smart-filter-source">
          <option value="">All sources</option>
          {(options.sources || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={inputCls} value={filters.status} onChange={sel("status")} data-testid="smart-filter-status">
          <option value="">All statuses</option>
          {["New", "Contacted", "Interested", "Booked", "Converted", "Lost"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={inputCls} value={filters.batch_id} onChange={sel("batch_id")} data-testid="smart-filter-batch">
          <option value="">All imports</option>
          {batches.map((b) => <option key={b.batch_id} value={b.batch_id}>{b.batch_id} ({b.count})</option>)}
        </select>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Btn type="button" variant="ghost" onClick={run} disabled={busy} data-testid="smart-filter-run">
          {busy ? "Checking…" : "Preview matches"}
        </Btn>
        {result && (
          <>
            <span className="text-xs text-neutral-600" data-testid="smart-filter-counts">
              {result.eligible_count} eligible · {result.excluded_count} excluded
            </span>
            <Btn
              type="button"
              data-testid="smart-filter-apply"
              disabled={result.eligible_count === 0}
              onClick={() => { onApply(result.eligible.map((l) => l.id)); toast.success(`${result.eligible_count} eligible leads selected`); }}
            >
              Select {result.eligible_count} eligible
            </Btn>
          </>
        )}
      </div>
      {result && result.excluded.length > 0 && (
        <details className="mt-2 text-xs text-neutral-500">
          <summary className="cursor-pointer font-semibold" data-testid="smart-filter-excluded-toggle">Why were {result.excluded_count} excluded?</summary>
          <ul className="mt-1.5 max-h-28 space-y-1 overflow-y-auto" data-testid="smart-filter-excluded-list">
            {result.excluded.map((l) => (
              <li key={l.id}>{l.full_name} — {l.excluded_reason}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function CaptureIntegrations() {
  const [data, setData] = useState(null);
  const load = () => api.get("/leads/capture-integrations").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const createHook = async () => {
    const { data: d } = await api.post("/leads/capture-integrations/google/hook");
    toast.success("Webhook URL created — paste it into your Google Ads lead form delivery settings");
    load();
    navigator.clipboard?.writeText(d.hook_url);
  };

  if (!data) return null;
  return (
    <Card className="p-5" data-testid="capture-integrations">
      <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Automatic lead capture</p>
      <div className="mt-3 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Globe size={14} className="text-neutral-400" />
            <p className="text-sm font-semibold text-[#111111]">Landing pages</p>
          </div>
          <Pill tone="green" data-testid="capture-lp-state">Connected</Pill>
        </div>
        <div className="rounded-xl border border-neutral-200 p-3.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-[#111111]">Meta Lead Ads</p>
            <Pill tone={data.meta_lead_ads.state === "ready" ? "amber" : "grey"} data-testid="capture-meta-state">
              {data.meta_lead_ads.state === "ready" ? "Ready to verify" : "Needs setup"}
            </Pill>
          </div>
          <ul className="mt-1.5 list-inside list-disc text-[11px] text-neutral-500">
            {data.meta_lead_ads.requirements.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
        <div className="rounded-xl border border-neutral-200 p-3.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-[#111111]">Google Ads lead forms</p>
            <Pill tone={data.google_lead_forms.state === "ready" ? "amber" : "grey"} data-testid="capture-google-state">
              {data.google_lead_forms.state === "ready" ? "Webhook ready" : "Needs setup"}
            </Pill>
          </div>
          <ul className="mt-1.5 list-inside list-disc text-[11px] text-neutral-500">
            {data.google_lead_forms.requirements.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
          {data.google_lead_forms.hook_url ? (
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 truncate rounded-lg bg-neutral-100 px-2.5 py-1.5 text-[11px]" data-testid="google-hook-url">{data.google_lead_forms.hook_url}</code>
              <Btn variant="ghost" data-testid="google-hook-copy" onClick={() => { navigator.clipboard?.writeText(data.google_lead_forms.hook_url); toast.success("Copied"); }}>Copy</Btn>
            </div>
          ) : (
            <Btn variant="ghost" className="mt-2" data-testid="google-hook-create" onClick={createHook}>Create webhook URL</Btn>
          )}
        </div>
      </div>
    </Card>
  );
}

function AutoCallingSettings() {
  const [s, setS] = useState(null);
  const [agents, setAgents] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/calling/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/voice-agents").then((r) => setAgents(r.data)).catch(() => {});
  }, []);
  if (!s) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/calling/settings", s);
      setS(data);
      toast.success(s.enabled ? "Automatic lead calling is ON for your account — calls still need approved tasks, consent and the platform switch" : "Settings saved — automatic calling stays off");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5" data-testid="auto-calling-settings">
      <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Automatic lead calling</p>
      <div className="mt-3 space-y-3">
        <label className="flex items-start gap-3 rounded-xl border border-neutral-200 p-3.5" data-testid="calling-enable-row">
          <input type="checkbox" className="mt-0.5 h-4 w-4 accent-neutral-900" checked={!!s.enabled} data-testid="calling-enable-toggle"
            onChange={(e) => setS((x) => ({ ...x, enabled: e.target.checked }))} />
          <span>
            <span className="text-sm font-semibold text-[#111111]">Enable automatic lead calling</span>
            <span className="block text-[11px] text-neutral-500">Off by default. Calls only go to leads with recorded call consent, during your calling hours, and only after you approve each task unless you turn approval off. Platform-level calling must also be enabled by the owner.</span>
          </span>
        </label>
        <label className="flex items-start gap-3 rounded-xl border border-neutral-200 p-3.5">
          <input type="checkbox" className="mt-0.5 h-4 w-4 accent-neutral-900" checked={!!s.require_approval} data-testid="calling-approval-toggle"
            onChange={(e) => setS((x) => ({ ...x, require_approval: e.target.checked }))} />
          <span>
            <span className="text-sm font-semibold text-[#111111]">Require my approval before each call</span>
            <span className="block text-[11px] text-neutral-500">Recommended. New leads queue as pending approval.</span>
          </span>
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="AI voice agent">
            <select className={inputCls} value={s.agent_id || ""} data-testid="calling-agent-setting" onChange={(e) => setS((x) => ({ ...x, agent_id: e.target.value || null }))}>
              <option value="">None selected</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.agent_name}</option>)}
            </select>
          </Field>
          <Field label="Calling hours">
            <div className="flex items-center gap-2">
              <input type="time" className={inputCls} value={s.hours_start} data-testid="calling-hours-from" onChange={(e) => setS((x) => ({ ...x, hours_start: e.target.value }))} />
              <span className="text-xs text-neutral-400">to</span>
              <input type="time" className={inputCls} value={s.hours_end} data-testid="calling-hours-to" onChange={(e) => setS((x) => ({ ...x, hours_end: e.target.value }))} />
            </div>
          </Field>
          <Field label="Calling days" hint="Calls only ever go out on the days you select, within the hours above, in your business timezone.">
            <div className="flex flex-wrap gap-1.5" data-testid="calling-days-picker">
              {[["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]].map(([d, label]) => {
                const on = (s.calling_days || ["mon", "tue", "wed", "thu", "fri"]).includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    data-testid={`calling-day-${d}`}
                    aria-pressed={on}
                    onClick={() => setS((x) => {
                      const cur = x.calling_days || ["mon", "tue", "wed", "thu", "fri"];
                      const next = on ? cur.filter((k) => k !== d) : [...cur, d];
                      return { ...x, calling_days: next };
                    })}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-[#111111] text-white" : "border border-neutral-300 text-neutral-400 hover:border-neutral-400"}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Max attempts per lead">
            <input type="number" min="1" max="10" className={inputCls} value={s.max_attempts} data-testid="calling-max-attempts" onChange={(e) => setS((x) => ({ ...x, max_attempts: parseInt(e.target.value) || 2 }))} />
          </Field>
          <Field label="Retry after (hours)">
            <input type="number" min="1" max="168" className={inputCls} value={s.retry_delay_hours} data-testid="calling-retry-hours" onChange={(e) => setS((x) => ({ ...x, retry_delay_hours: parseInt(e.target.value) || 24 }))} />
          </Field>
        </div>
        <Field label="Qualification script (what the AI should ask/learn)">
          <textarea rows={3} className={inputCls} value={s.script || ""} data-testid="calling-script"
            placeholder="e.g. Introduce the salon, ask what service they're interested in, offer to book an appointment…"
            onChange={(e) => setS((x) => ({ ...x, script: e.target.value }))} />
        </Field>
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-neutral-400">
            Telephony: {s.telephony_configured ? "connected" : "not connected"} · Platform switch: {s.platform_calls_enabled ? "on" : "off"}
          </p>
          <Btn data-testid="calling-settings-save" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save settings"}</Btn>
        </div>
      </div>
    </Card>
  );
}

function CallTasks() {
  const [tasks, setTasks] = useState(null);
  const [stats, setStats] = useState(null);
  const load = () => {
    api.get("/calling/tasks").then((r) => setTasks(r.data.tasks)).catch(() => setTasks([]));
    api.get("/calling/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const act = async (t, action) => {
    try {
      await api.post(`/calling/tasks/${t.id}/${action}`);
      toast.success(action === "approve" ? "Approved — it will dial within calling hours" : "Call task cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Card className="p-5" data-testid="call-tasks">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Lead call queue</p>
        {stats && (
          <p className="text-[11px] text-neutral-400" data-testid="call-tasks-stats">
            {Object.entries(stats.by_status).map(([k, v]) => `${v} ${k.replace("_", " ")}`).join(" · ") || "no tasks yet"}
          </p>
        )}
      </div>
      {tasks === null ? <p className="py-4 text-center text-xs text-neutral-400">Loading…</p> : tasks.length === 0 ? (
        <p className="py-4 text-center text-xs text-neutral-400" data-testid="call-tasks-empty">No call tasks — captured leads appear here when automatic calling is on and every safeguard passes.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {tasks.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-neutral-200 px-3.5 py-2.5" data-testid={`call-task-${t.id}`}>
              <div>
                <p className="text-xs font-semibold text-[#111111]">{t.lead_name}</p>
                <p className="text-[11px] text-neutral-500">
                  {t.attempts} attempt{t.attempts === 1 ? "" : "s"}{t.outcome ? ` · ${t.outcome.replace(/_/g, " ")}` : ""}{t.error ? ` · ${t.error}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <Pill tone={TASK_TONE[t.status]} data-testid={`call-task-status-${t.id}`}>{t.status.replace("_", " ")}</Pill>
                {t.status === "pending_approval" && (
                  <button className="text-neutral-400 hover:text-green-700" title="Approve call" data-testid={`call-task-approve-${t.id}`} onClick={() => act(t, "approve")}><CheckCircle2 size={16} /></button>
                )}
                {["pending_approval", "queued"].includes(t.status) && (
                  <button className="text-neutral-400 hover:text-red-600" title="Cancel" data-testid={`call-task-cancel-${t.id}`} onClick={() => act(t, "cancel")}><XCircle size={16} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-2 text-[11px] text-neutral-400">Outcomes come from real call logs only — nothing is simulated.</p>
    </Card>
  );
}

export default function Calling() {
  const [campaigns, setCampaigns] = useState(null);
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/calling-campaigns").then((r) => setCampaigns(r.data));
  useEffect(() => {
    load();
    api.get("/leads").then((r) => setLeads(r.data));
    api.get("/voice-agents").then((r) => setAgents(r.data));
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggleLead = (id) =>
    setForm((f) => ({
      ...f,
      lead_ids: f.lead_ids.includes(id) ? f.lead_ids.filter((x) => x !== id) : [...f.lead_ids, id],
    }));

  const openEdit = (c) => {
    setForm({
      name: c.name,
      agent_id: c.agent_id || "",
      lead_ids: c.lead_ids || [],
      schedule: c.schedule || "",
      max_attempts: c.max_attempts ?? 2,
      hours_start: c.hours_start || "09:00",
      hours_end: c.hours_end || "17:00",
      follow_up_rules: c.follow_up_rules || "",
    });
    setError("");
    setModal({ mode: "edit", campaign: c });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = {
      ...form,
      agent_id: form.agent_id || null,
      schedule: form.schedule || null,
      max_attempts: Number(form.max_attempts),
    };
    try {
      if (modal.mode === "edit") await api.put(`/calling-campaigns/${modal.campaign.id}`, payload);
      else await api.post("/calling-campaigns", payload);
      setModal(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    await api.delete(`/calling-campaigns/${c.id}`);
    load();
  };

  return (
    <div data-testid="calling-page">
      <PageHeader title="Automatic Calling" sub="Plan outbound calling campaigns. They save as Drafts — no calls are placed.">
        <Btn data-testid="add-calling-campaign-button" onClick={() => { setForm(EMPTY_FORM); setError(""); setModal({ mode: "add" }); }}>
          <Plus size={15} /> New Campaign
        </Btn>
      </PageHeader>

      <Notice
        testid="calling-notice"
        title="Calling is disabled"
        body="Campaigns stay in Draft until real telephony (LiveKit Agents / Pipecat), consent checks, suppression lists and rate limits are in place. Leads marked “Do not call” can never be added to a campaign."
      />

      <div className="mt-5 flex flex-wrap items-center gap-2 rounded-2xl border border-neutral-200 bg-white p-4" data-testid="calling-workflow">
        {["Select CRM leads", "Select voice agent", "Configure schedule", "Review consent", "Start campaign"].map((step, i) => (
          <span key={step} className="flex items-center gap-2">
            <span className={`rounded-full px-3 py-1.5 text-xs font-semibold ${i === 4 ? "bg-neutral-100 text-neutral-400" : "bg-[#111111] text-white"}`}>
              {step}
            </span>
            {i < 4 && <ArrowRight size={13} className="text-neutral-300" />}
          </span>
        ))}
      </div>

      <div className="mt-5 space-y-5">
        <CaptureIntegrations />
        <AutoCallingSettings />
        <CallTasks />
      </div>

      <div className="mt-5">
        {campaigns === null ? (
          <p className="py-10 text-center text-sm text-neutral-400">Loading…</p>
        ) : campaigns.length === 0 ? (
          <Empty
            testid="calling-empty"
            icon={PhoneOutgoing}
            title="No calling campaigns"
            body="Create a draft campaign: pick leads, assign a voice agent and set a schedule — ready for when calling goes live."
          />
        ) : (
          <div className="space-y-3">
            {campaigns.map((c) => (
              <Card key={c.id} className="p-5" data-testid={`calling-card-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-[#111111]">{c.name}</p>
                    <p className="mt-1 text-xs text-neutral-500">
                      {c.lead_ids.length} leads · Agent: {c.agent_name || "none"} · Max {c.max_attempts} attempts
                      {c.hours_start && c.hours_end ? ` · ${c.hours_start}–${c.hours_end}` : ""}
                      {c.schedule ? ` · Starts ${String(c.schedule).replace("T", " ")}` : ""}
                    </p>
                    {c.follow_up_rules && <p className="mt-1 text-xs text-neutral-400">Follow-up: {c.follow_up_rules}</p>}
                    <p className="mt-1 text-xs font-medium text-neutral-500" data-testid={`calling-consent-${c.id}`}>
                      Consent check: {c.lead_ids.filter((id) => leads.find((l) => l.id === id)?.call_consent).length} of {c.lead_ids.length} selected leads have recorded call consent
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Pill tone="outline">Draft — not connected</Pill>
                    <button
                      data-testid={`calling-start-${c.id}`}
                      disabled
                      title="Disabled until a compliant calling service is connected"
                      className="cursor-not-allowed rounded-full bg-neutral-100 px-4 py-2 text-xs font-semibold text-neutral-400"
                    >
                      Start Campaign
                    </button>
                    <button data-testid={`calling-edit-${c.id}`} aria-label="Edit campaign" onClick={() => openEdit(c)} className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100">
                      <Pencil size={14} />
                    </button>
                    <button data-testid={`calling-delete-${c.id}`} aria-label="Delete campaign" onClick={() => remove(c)} className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <Modal testid="calling-modal" title={modal.mode === "edit" ? "Edit calling campaign (draft)" : "New calling campaign (draft)"} onClose={() => setModal(null)}>
          <form onSubmit={save} className="space-y-4">
            <Field label="Campaign name *">
              <input data-testid="calling-name-input" className={inputCls} value={form.name} onChange={set("name")} required />
            </Field>
            <Field label="Assign AI voice agent" hint={agents.length === 0 ? "Create an agent first in AI Voice Agents" : undefined}>
              <select data-testid="calling-agent-select" className={inputCls} value={form.agent_id} onChange={set("agent_id")}>
                <option value="">No agent selected</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.agent_name}</option>)}
              </select>
            </Field>
            <Field label={`Select leads (${form.lead_ids.length} chosen)`}>
              <SmartSelect onApply={(ids) => setForm((f) => ({ ...f, lead_ids: Array.from(new Set([...f.lead_ids, ...ids])) }))} />
              <div className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded-xl border border-neutral-200 p-2">
                {leads.length === 0 && <p className="px-2 py-3 text-xs text-neutral-400">No leads yet — add some in CRM Leads first.</p>}
                {leads.map((l) => (
                  <label
                    key={l.id}
                    className={`flex items-center justify-between rounded-lg px-2.5 py-2 text-sm ${l.do_not_call ? "opacity-50" : "hover:bg-neutral-50"}`}
                  >
                    <span className="flex items-center gap-2.5">
                      <input
                        type="checkbox"
                        data-testid={`calling-lead-${l.id}`}
                        disabled={l.do_not_call}
                        checked={form.lead_ids.includes(l.id)}
                        onChange={() => toggleLead(l.id)}
                        className="h-4 w-4 accent-[#111111]"
                      />
                      {l.full_name}
                    </span>
                    {l.do_not_call && <span className="text-[11px] font-semibold text-red-600">Opted out</span>}
                  </label>
                ))}
              </div>
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Schedule start">
                <input data-testid="calling-schedule-input" type="datetime-local" className={inputCls} value={form.schedule} onChange={set("schedule")} />
              </Field>
              <Field label="Maximum call attempts">
                <input data-testid="calling-attempts-input" type="number" min={1} max={10} className={inputCls} value={form.max_attempts} onChange={set("max_attempts")} />
              </Field>
              <Field label="Business hours — from">
                <input data-testid="calling-hours-start" type="time" className={inputCls} value={form.hours_start} onChange={set("hours_start")} />
              </Field>
              <Field label="Business hours — until">
                <input data-testid="calling-hours-end" type="time" className={inputCls} value={form.hours_end} onChange={set("hours_end")} />
              </Field>
            </div>
            <Field label="Follow-up rules">
              <textarea data-testid="calling-followup-input" rows={2} className={inputCls} value={form.follow_up_rules} onChange={set("follow_up_rules")} placeholder="e.g. If no answer, retry next business day; stop after max attempts" />
            </Field>
            {error && <p data-testid="calling-form-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2.5">
              <Btn type="button" variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
              <Btn data-testid="calling-save-button" type="submit" disabled={busy}>{busy ? "Saving…" : modal.mode === "edit" ? "Save Changes" : "Save Draft"}</Btn>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
